import type * as contentful from 'contentful'
import { BLOCKS } from '@contentful/rich-text-types'
import type {
  TypeBaseComponent,
  TypeImageComponent,
  TypeImageTextComponent,
  TypeTextComponent
} from '@/types/contentful'
import AnimationLocalMatters from '@/assets/images/restaurant/local-matters-animation.webm?url'
import { TABLE_BOOKING_URL } from '@/constants/restaurant'

const baseEntitySys: Omit<contentful.EntitySys, 'type'> = {
  space: { sys: { type: 'Link', linkType: 'Space', id: 'local' } },
  'id': 'abc123',
  createdAt: '2024-02-04T01:23:45.678Z',
  updatedAt: '2024-02-04T01:23:45.678Z',
  environment: { sys: { id: 'development', type: 'Link', linkType: 'Environment' } },
  revision: 1,
  locale: 'en-AU'
}

const basePage: TypeBaseComponent<'WITHOUT_UNRESOLVABLE_LINKS', string> = {
  metadata: { tags: [] },
  sys: {
    ...baseEntitySys,
    type: 'Entry',
    contentType: { sys: { type: 'Link', linkType: 'ContentType', id: 'baseComponent' } }
  },
  fields: {
    title: 'Page Base',
    theme: 'Light',
    backgroundColour: 'var(--color-base-white)',
    visibility: 'Everyone'
  }
}

const cateringImageAndTextData: TypeImageTextComponent<'WITHOUT_UNRESOLVABLE_LINKS', string> = {
  metadata: { tags: [] },
  sys: {
    ...baseEntitySys,
    type: 'Entry',
    contentType: { sys: { type: 'Link', linkType: 'ContentType', id: 'imageTextComponent' } }
  },
  fields: {
    title: 'Restaurant Catering Image Text Component',
    image: {
      metadata: { tags: [] },
      sys: {
        ...baseEntitySys,
        type: 'Asset'
      },
      fields: {
        title: 'Category Image',
        description: 'Image of a catering event',
        file: {
          url: '/assets/restaurant/5_4-catering.webp',
          details: { size: 28642, 'image': { width: 520, height: 416 } },
          fileName: '5_4-catering.webp',
          contentType: 'image/webp'
        }
      }
    },
    ratio: 'M',
    imageAlignment: 'Left',
    text: {
      metadata: { tags: [] },
      sys: {
        ...baseEntitySys,
        type: 'Entry',
        contentType: { sys: { type: 'Link', linkType: 'ContentType', id: 'textBlock' } }
      },
      fields: {
        title: "Feedin' a crowd? We'll cater!",
        heading: "Feedin' a crowd? We'll cater!",
        body: {
          data: {},
          content: [
            {
              data: {},
              content: [
                {
                  data: {},
                  marks: [],
                  value:
                    "From birthday parties to corporate events, footy games and picnics with pals, Grill'd's got the goods to satisfy the crew. Choose from sliders, salads and stacks of chips, with dietary-friendly options! Order before 5pm for next day pickup.",
                  nodeType: 'text'
                }
              ],
              nodeType: BLOCKS.PARAGRAPH
            }
          ],
          nodeType: BLOCKS.DOCUMENT
        },
        alignment: 'Left',
        headerColour: '#000',
        bodyColour: '#323840'
      }
    },
    primaryButton: {
      metadata: { tags: [] },
      sys: {
        ...baseEntitySys,
        type: 'Entry',
        contentType: { sys: { type: 'Link', linkType: 'ContentType', id: 'buttonBlock' } }
      },
      fields: { title: 'Order Catering', href: '/catering', target: 'App' }
    },
    secondaryButton: undefined,
    base: basePage
  }
}

const groupBookingImageAndTextContent: TypeImageTextComponent<'WITHOUT_UNRESOLVABLE_LINKS', string> = {
  metadata: { tags: [] },
  sys: {
    ...baseEntitySys,
    type: 'Entry',
    contentType: { sys: { type: 'Link', linkType: 'ContentType', id: 'imageTextComponent' } }
  },
  fields: {
    title: 'Restaurant Book a Table Image Text Component',
    image: {
      metadata: { tags: [] },
      sys: {
        ...baseEntitySys,
        type: 'Asset'
      },
      fields: {
        title: 'Dine-in Image',
        description: 'Image of a dine-in restaurant',
        file: {
          url: '/assets/restaurant/5_4-group-bookings.webp',
          details: { size: 27208, 'image': { width: 520, height: 416 } },
          fileName: '5_4-group-bookings.webp',
          contentType: 'image/webp'
        }
      }
    },
    ratio: 'M',
    imageAlignment: 'Right',
    text: {
      metadata: { tags: [] },
      sys: {
        ...baseEntitySys,
        type: 'Entry',
        contentType: { sys: { type: 'Link', linkType: 'ContentType', id: 'textBlock' } }
      },
      fields: {
        title: "Comin' with a big crew?",
        heading: "Comin' with a big crew?",
        body: {
          data: {},
          content: [
            {
              data: {},
              content: [
                {
                  data: {},
                  marks: [],
                  value:
                    "Work or play, mates or team-mates, birthday or game day. Whatever the group gathering, book 'em in for a Grill'd feed!",
                  nodeType: 'text'
                }
              ],
              nodeType: BLOCKS.PARAGRAPH
            }
          ],
          nodeType: BLOCKS.DOCUMENT
        },
        alignment: 'Left',
        headerColour: '#000',
        bodyColour: '#323840'
      }
    },
    primaryButton: {
      metadata: { tags: [] },
      sys: {
        ...baseEntitySys,
        type: 'Entry',
        contentType: { sys: { type: 'Link', linkType: 'ContentType', id: 'buttonBlock' } }
      },
      fields: { title: 'Book your table', href: TABLE_BOOKING_URL, target: 'Tab' }
    },
    secondaryButton: undefined,
    base: basePage
  }
}

const localMattersTextContent: TypeTextComponent<'WITHOUT_UNRESOLVABLE_LINKS', string> = {
  metadata: { tags: [] },
  sys: {
    ...baseEntitySys,
    type: 'Entry',
    contentType: { sys: { type: 'Link', linkType: 'ContentType', id: 'textComponent' } }
  },
  fields: {
    title: 'Example Text Component',
    text: {
      metadata: { tags: [] },
      sys: {
        ...baseEntitySys,
        type: 'Entry',
        contentType: { sys: { type: 'Link', linkType: 'ContentType', id: 'textBlock' } }
      },
      fields: {
        title: 'Local matters block',
        display: 'Local Matters',
        body: {
          data: {},
          content: [
            {
              data: {},
              content: [
                {
                  data: {},
                  marks: [],
                  value: "See the community groups we're supporting this month!",
                  nodeType: 'text'
                }
              ],
              nodeType: BLOCKS.PARAGRAPH
            }
          ],
          nodeType: BLOCKS.DOCUMENT
        },
        alignment: 'Centre',
        headerColour: '#E7131A',
        bodyColour: '#323840'
      }
    },
    primaryButton: undefined,
    secondaryButton: undefined,
    linkButton: undefined,
    base: basePage
  }
}

const learnMoreTextContent: TypeTextComponent<'WITHOUT_UNRESOLVABLE_LINKS', string> = {
  metadata: { tags: [] },
  sys: {
    ...baseEntitySys,
    type: 'Entry',
    contentType: { sys: { type: 'Link', linkType: 'ContentType', id: 'textComponent' } }
  },
  fields: {
    title: 'Example Text Component',
    text: {
      metadata: { tags: [] },
      sys: {
        ...baseEntitySys,
        type: 'Entry',
        contentType: { sys: { type: 'Link', linkType: 'ContentType', id: 'textBlock' } }
      },
      fields: {
        title: 'Learn more text block',
        alignment: 'Centre'
      }
    },
    primaryButton: {
      metadata: { tags: [] },
      sys: {
        ...baseEntitySys,
        type: 'Entry',
        contentType: { sys: { type: 'Link', linkType: 'ContentType', id: 'buttonBlock' } }
      },
      fields: { title: 'Learn more', href: '/local-matters', target: 'Tab' }
    },
    secondaryButton: undefined,
    linkButton: undefined,
    base: basePage
  }
}

const localMattersAnimationContent: TypeImageComponent<'WITHOUT_UNRESOLVABLE_LINKS', string> = {
  metadata: { tags: [] },
  sys: {
    ...baseEntitySys,
    type: 'Entry',
    contentType: { sys: { type: 'Link', linkType: 'ContentType', id: 'imageComponent' } }
  },
  fields: {
    title: 'Local Matters image with webm video component',
    image: {
      metadata: { tags: [] },
      sys: {
        ...baseEntitySys,
        type: 'Asset'
      },
      fields: {
        title: 'Local Matters webm image',
        description: 'local matters animation',
        file: {
          url: AnimationLocalMatters,
          details: { 'size': 9860000 },
          fileName: 'local-matters-animation.webm',
          contentType: 'video/webm'
        }
      }
    },
    ratio: 'S',
    base: basePage
  }
}

/**
 * @description Create the catering component content
 * @param restaurantId {number | undefined} - The restaurant id
 */
function createCateringComponentContent(restaurantId?: number) {
  const content = structuredClone(cateringImageAndTextData)

  const primaryButtonFields = {
    title: 'Order Catering',
    href: restaurantId ? `/catering&restaurant=${restaurantId}` : '/catering',
    target: 'App'
  }

  Object.assign(content.fields.primaryButton!.fields, primaryButtonFields)

  return content
}

/**
 * @description Create the group booking component content
 * @param obeeId {string | null} - The obee id used for group bookings
 */
function createGroupBookingComponentContent(obeeId: string | null) {
  if (!obeeId) return undefined

  const content = structuredClone(groupBookingImageAndTextContent)

  const primaryButtonFields = {
    title: 'Book your table',
    href: `${TABLE_BOOKING_URL}/${obeeId}`,
    target: 'Tab'
  }

  Object.assign(content.fields.primaryButton!.fields, primaryButtonFields)

  return content
}

/**
 * @description Create the default content for the restaurant details page
 * @param catering {boolean} - Whether the restaurant offers catering
 * @param restaurantId {number} - The restaurant id
 * @param obeeId {string | null} - The obee id used for group bookings
 */
export function createRestaurantDefaultContent({
  catering,
  restaurantId,
  obeeId
}: {
  catering: boolean
  restaurantId: number
  obeeId: string | null
}) {
  return {
    catering: catering ? createCateringComponentContent(restaurantId) : undefined,
    groupBooking: obeeId ? createGroupBookingComponentContent(obeeId) : undefined,
    localMattersText: localMattersTextContent,
    learnMoreButton: learnMoreTextContent,
    localMattersAnimation: localMattersAnimationContent
  }
}
